import { useEffect, useState } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Tooltip
} from "@fluentui/react-components";
import PropTypes from "prop-types";
import EnergySearchSkeletonGrid from "./search-skeleton-grid";
import FluentUiIcon from "../../../../../../assets/fluent-ui-icon";
import { SearchWithinEnergyProjects, SearchWithinSIEnergyProjects } from "../../../../../../services/api/dashboard-api";
import EmptyState from "../../../../../../assets/img/Errors/NoDealWithinDashboard.svg";
import { FluentButton } from "../../../../../../components/FluentUI/Fluent-Button";
import EnergySearchGrid from "./search-grid";
import "./search-dialog.scss";

const EnergySearchDialog = ({ open, searchValue, onClose, onModifySearch, isSI = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState([]);
  const isEmptyState = !isLoading && results.length === 0;

  useEffect(() => {
    document.body.classList.toggle("energy-dialog-open", open);
  }, [open]);

  useEffect(() => {
    const fetchData = async () => {
      if (open) {
        setIsLoading(true);
        try {
          const data = isSI ? await SearchWithinSIEnergyProjects(searchValue) : await SearchWithinEnergyProjects(searchValue);
          setResults(data?.data?.Projects || []);
        } catch (err) {
          console.error("Search failed:", err);
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(true);
        setResults([]);
      }
    };

    fetchData();
  }, [open, searchValue, isSI]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogSurface className="energy-search-dialog">
        <DialogBody>
          <div className="dialog-header">
            <DialogTitle as="h2" className="dialog-heading-wrapper">
              <span>
                {isEmptyState
                  ? `No Results for ‘${searchValue}’ within all Energy Deals`
                  : isLoading
                    ? `Searching for ‘${searchValue}’ within all Energy Deals`
                    : `${results.length} Result${results.length > 1 ? "s" : ""} for ‘${searchValue}’ within all Energy Deals`}
              </span>
            </DialogTitle>

            <Tooltip content="Dismiss">
              <FluentButton
                appearance="secondary"
                icon={<FluentUiIcon name="DismissRegular" color="#0F6CBD" size={20} />}
                onClick={onClose}
                className="close-button"
              />
            </Tooltip>
          </div>
          <DialogContent>
            {isLoading ? (
              <EnergySearchSkeletonGrid />
            ) : isEmptyState ? (
              <div className="empty-state-container-dialog">
                <p className="empty-state-header-dialog">We couldn’t find any other Deals matching your search.</p>
                <img src={EmptyState} alt="No Results" className="empty-state-image" />
                <div className="empty-state-actions">
                  <FluentButton
                    appearance="primary"
                    text="Modify Search"
                    onClick={() => {
                      onClose();
                      onModifySearch();
                    }}
                  />
                  <FluentButton
                    appearance="secondaryWithOutline"
                    onClick={onClose}
                    text="Close"
                  />
                </div>
              </div>
            ) : (
              <>
                <p className="dialog-subtext">
                  We found <strong>{results.length} Deal(s)</strong> that match your search.
                  You’re seeing limited details — Ask a Staffer or Deal Team Member to add you, so that you can access the full Deal and time tracking.
                </p>
                <EnergySearchGrid data={results} searchValue={searchValue} />
              </>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

EnergySearchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  searchValue: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onModifySearch: PropTypes.func.isRequired,
  isSI: PropTypes.bool,
};

export default EnergySearchDialog;